import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Grid, Typography, Alert, Checkbox, FormControlLabel, Tooltip, Button } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  RateReview as ReviewIcon,
  EditRoadOutlined as RoadIcon,
  SouthAmericaOutlined as RegionalIcon,
  DirectionsTransitFilledOutlined as TransitIcon,
  TaskAlt as ActiveIcon,
  PauseCircleOutlined as InactiveIcon,
  PendingActions as DraftIcon,
  InfoOutlined as InfoIcon,
  Unpublished as UnpublishIcon,
  Download as DownloadIcon,
  WarningAmber as PendingReviewIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { StyledDataGridProjects, StyledTypography, StyledNoticeTypography } from "./styles";

//context
import { useAuth } from "../../context/AuthContext";

//components
import ConfirmationDialog from "../../components/Dialog/ConfirmationDialog";
import { getFormattedDate } from "../../utils/getFormattedDateTime";
import RouterBreadcrumbs from "../../components/Breadcrumbs/RouterBreadcrumbs";
import Title from "../../components/Typography/Title";
import IconsWithTooltip from "../../components/Icons/IconsWithTooltip";
import CustomToolbar from "../../components/Toolbar/CustomToolbar";

//custom hook
import { useFetch } from "../../hooks/useFetch";
import { useUpdate } from "../../hooks/useUpdate";

//constants
import { API_URLS } from "../../Constants";

//utils
import { markForReview } from "../../utils/dateUtils";
import mapProjectsData from "../../utils/mapProjectsData";

//theme
import themes from "../../themes/defaultTheme";

const pathItems = [
  "Dashboard",
  "Projects",
  "View Projects"
]

export default function ViewProjects() {

  //Get loggedIn user details
  const { auth } = useAuth();
  const loggedInUser = auth?.user || null;

  //React hooks
  const [projectsData, setProjectsData] = useState([])
  const [reloadFlag, setReloadFlag] = useState(false);
  const [activeFilter, setActiveFilter] = useState({ column: '', value: '' });

  //API fetchers
  const fetcher = useFetch("get", API_URLS["API_GET_PROJECTS"]);
  const deleteFetcher = useUpdate("post", API_URLS["API_DELETE_PROJECTS"]);
  const getFetcher = useFetch("get", API_URLS["API_GET_PROJECT_BY_ID"]);
  const postFetcher = useUpdate("post", API_URLS["API_ADD_UPDATE_PROJECT"]);

  //Fetch data from API when the component renders.
  useEffect(() => {
    fetcher.setRequest({});
  }, [reloadFlag]);

  //Once response is received, set appropriate state variables.
  useEffect(() => {
    const response = fetcher.serverResponse;
    if (response) {

      // Sort the projects alphabetically by name and add isPendingReview flag
      const sortedProjects = response.toSorted((a, b) => a.projectTitle?.localeCompare(b.projectTitle))
        .map(project => ({
          ...project,
          isPendingReview: project.status?.toLowerCase() === "active" && markForReview(project.reviewedDate)
        }))
        .sort((a, b) => (b.isPendingReview ? 1 : 0) - (a.isPendingReview ? 1 : 0));
      setProjectsData(sortedProjects);
    }
  }, [fetcher.serverResponse]);

  //Once data from DELETE request is fetched, refetch the data 
  useEffect(() => {
    if (deleteFetcher?.serverResponse?.length > 0) {
      setReloadFlag(prev => !prev);
    }
  }, [deleteFetcher.serverResponse]);

  //Once data from GET request is fetched, update the project 
  useEffect(() => {
    if (getFetcher?.serverResponse?.length > 0) {
      const response = getFetcher.serverResponse;

      //Set options
      const options = {
        projectData: response[0],
        projectStatus: "draft",
        passedCreatedDate: response[0].createdDate,
        passedYrFirstEntered: response[0].yearFirstEntered,
        passedYrPlanActive: response[0].yearPlanActive,
        passedQtrPlanActive: response[0].quarterPlanActive,
        passedReviewedDate: response[0].reviewedDate,
        passedReviewedBy: response[0].reviewedBy
      }
      const mappedProjectData = mapProjectsData(options);

      // Update data in the database.
      postFetcher.executeUpdate(mappedProjectData);
    }
  }, [getFetcher.serverResponse]);

  //Once data from POST request is fetched, get all projects 
  useEffect(() => {
    if (postFetcher?.serverResponse)
      setReloadFlag(prev => !prev);
    else if (postFetcher?.serverResponse?.error)
      console.error('Error in POST request: ', postFetcher.serverResponse.error);
  }, [postFetcher.serverResponse]);

  //React Hooks
  const navigate = useNavigate();
  const [archiveChecked, setArchiveChecked] = useState(false);
  const [allProjectsChecked, setAllProjectsChecked] = useState(false);
  const [reviewPendingChecked, setReviewPendingChecked] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => { }, // Default empty function
  });

  //Filter the rows based on the active filter value and column.
  const filteredRows = useMemo(() => {
    if (!projectsData) return [];

    let data = projectsData.filter(row => row.status?.toLowerCase() !== "deleted");

    if (archiveChecked)
      data = data.filter(row => row.status?.toLowerCase() === "archived");
    else if (reviewPendingChecked)
      data = data.filter(row => row.isPendingReview);
    else if (!allProjectsChecked)
      data = data.filter(row => row.status?.toLowerCase() !== "archived");

    if (!activeFilter.value || !activeFilter.column)
      return data;
    const searchTerm = activeFilter.value.toLowerCase();
    const column = activeFilter.column;
    return data.filter(row => {
      let rawValue = row[column];
      switch (column) { //handle specific column formatting for search
        case 'region':
        case 'regionNames':
          let regionsArray = row.regionNames || [];
          const impactedRegions = row.impactedRegions?.filter((reg) => reg.id !== null) || [];
          if (impactedRegions.length > 0) {
            const impactedNames = impactedRegions.map((reg) => reg.name);
            regionsArray = [...regionsArray, ...impactedNames];
          }
          rawValue = regionsArray.join(", ");
          break;
        case 'strategyTypesNames':
          rawValue = row.strategyTypesNames?.join(", ") || "";
          break;
        case 'csjNumber':
          rawValue = row.csjNumbers?.join(", ") || "";
          break;
        case 'lastModified':
          const dateStr = row.lastModifiedDate ? getFormattedDate(new Date(row.lastModifiedDate)) : "";
          const userStr = row.lastModifiedBy ? row.lastModifiedBy.split('@')[0] : "";
          rawValue = `${dateStr} ${userStr}`;
          break;
        case 'reviewedDate':
          rawValue = row.reviewedDate ? getFormattedDate(new Date(row.reviewedDate)) : "";
          break;
        case 'status':
          const statusValue = row.status || "";
          return statusValue.toLowerCase() === searchTerm.toLowerCase();
        case 'isClearLanes':
          rawValue = row.isClearLanes ? "yes" : "no";
          return rawValue === searchTerm.toLowerCase();
        default:
          break;
      }
      if (rawValue == null) return false;
      return String(rawValue).toLowerCase().includes(searchTerm);
    });
  }, [projectsData, activeFilter, archiveChecked, allProjectsChecked, reviewPendingChecked]);

  //Show All Projects when checkbox is checked.
  const handleShowAllProjects = () => {
    setAllProjectsChecked(!allProjectsChecked);
    setReviewPendingChecked(false);
    setArchiveChecked(false);
  };

  //Show Archived Projects when checkbox is checked.
  const handleShowArchivedProjects = () => {
    setAllProjectsChecked(false);
    setReviewPendingChecked(false);
    setArchiveChecked(!archiveChecked);
  };

  //Show Projects that need review when checkbox is checked.
  const handleShowReviewPendingProjects = () => {
    setAllProjectsChecked(false);
    setReviewPendingChecked(!reviewPendingChecked);
    setArchiveChecked(false);
  };

  // Project Deletion: Set action on clicking "Confirm" for the selected project.
  const handleDeleteConfirm = () => {
    setConfirmationDialog((prevState) => ({ ...prevState, open: false }));
  };

  // Project Deletion: Set ConfirmationDialog properties for selected project(s).
  const handleDeleteSelected = (selectedIds) => {
    if (selectedIds && selectedIds.length > 0) {
      setConfirmationDialog({
        open: true,
        title: "Delete Confirmation",
        message: `Are you sure you want to delete the selected project(s) ?`,
        onConfirm: () => {

          // Update data in the database.
          deleteFetcher.executeUpdate({ "ids": selectedIds });
          setSelectedRows([]);
          handleDeleteConfirm();
        },
      });
    }
  };

  // Project Unpublish: Set ConfirmationDialog properties for selected project(s).
  const handleUnpublishSelected = (selectedIds) => {
    if (selectedIds && selectedIds.length > 0) {
      setConfirmationDialog({
        open: true,
        title: "Unpublish Confirmation",
        message: `Are you sure you want to unpublish the selected project ?`,
        onConfirm: () => {

          //Get updated project details from database.
          const url = `${API_URLS["API_GET_PROJECT_BY_ID"]}?id=${selectedIds[0]}`;
          getFetcher.setRequest({ passedUrl: url });
          setSelectedRows([]);

          //Close the dialog
          setConfirmationDialog((prevState) => ({ ...prevState, open: false }));
        },
      });
    }
  };

  //Custom CSV Export
  const handleExportCSV = () => {

    //Helper function to escape CSV field
    const escapeCsvField = (value) => {
      const str = String(value);
      // Wrap in quotes if contains comma, quote, or newline
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    //Helper function to create a row
    const createRow = (projectData, phase) => {
      const phaseData = phase && Object.keys(phase).length > 0 ? {
        csjNumber: phase.csjNumber ? escapeCsvField(phase.csjNumber) : '',
        letDate: phase.letDate ? getFormattedDate(new Date(phase.letDate)) : '',
        completionDate: phase.completionDate ? getFormattedDate(new Date(phase.completionDate)) : '',
        costEstimate: phase.costEstimate ? escapeCsvField(phase.costEstimate) : '',
        studyCost: phase.studyCost ? escapeCsvField(phase.studyCost) : ''
      } : {
        csjNumber: '',
        letDate: '',
        completionDate: '',
        costEstimate: '',
        studyCost: ''
      };
      return [
        projectData.projectTitle,
        projectData.projectType,
        projectData.strategyTypes,
        projectData.regions,
        phaseData.csjNumber,
        phaseData.letDate,
        phaseData.completionDate,
        phaseData.costEstimate,
        phaseData.studyCost,
        projectData.reviewedDate,
        projectData.lastModifiedDate,
        projectData.lastModifiedBy
      ].join(',');
    };

    const headers = [
      'Project Title',
      'Type',
      'Strategy Type(s)',
      'Impacted Region(s)',
      'CSJ or Plan ID',
      'Let Date/Study Start Date',
      'Completion Date',
      'Cost Estimate ($)',
      'Study Cost ($)',
      'Good As Of',
      'Last Modified Date',
      'Last Modified By',
    ];
    const dataRows = filteredRows.flatMap(project => {

      //Combine regions and impacted regions
      const regions = [...(project.regionNames || [])];
      const impactedRegions = project.impactedRegions
        ?.filter(reg => reg.id !== null)
        ?.map(reg => reg.name) || [];
      const allRegions = regions.concat(impactedRegions).join(', ');

      //Get other project details
      const reviewedDate = project.reviewedDate ? getFormattedDate(new Date(project.reviewedDate)) : '';
      const lastModifiedDate = project.lastModifiedDate ? getFormattedDate(new Date(project.lastModifiedDate)) : '';
      const lastModifiedBy = project.lastModifiedBy ? project.lastModifiedBy.split('@')[0] : '';

      //Create project data object with escaped fields for CSV
      const projectData = {
        projectTitle: project.projectTitle ? escapeCsvField(project.projectTitle) : '',
        projectType: project.projectType ? escapeCsvField(project.projectType) : '',
        strategyTypes: project.strategyTypesNames?.length > 0 ? escapeCsvField(project.strategyTypesNames.join(', ')) : '',
        regions: allRegions ? escapeCsvField(allRegions) : '',
        reviewedDate: reviewedDate ? escapeCsvField(reviewedDate) : '',
        lastModifiedDate: lastModifiedDate ? escapeCsvField(lastModifiedDate) : '',
        lastModifiedBy: lastModifiedBy ? escapeCsvField(lastModifiedBy) : ''
      };

      //Generate project phases data
      const projectPhases = project.projectDetails || [];
      if (projectPhases.length === 0) { //If no phases, return single row with empty phase data
        return [createRow(projectData, null)];
      }
      return projectPhases.map(phase => createRow(projectData, phase));
    });

    //Create csv content
    const csvContent = [headers.join(','), ...dataRows].join('\n');

    //Generate filename with currentDate
    const currentDate = getFormattedDate(new Date());
    const filename = `Mitigation Plans_${currentDate}.csv`;

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  //Function to sort date-based columns
  const sortByDateAndOptionalUser = (v1, v2, compareUser = false) => {
    if (!v1 && !v2) return 0;
    if (!v1) return -1;
    if (!v2) return 1;

    if (compareUser) { //for Last Modified column: split into date and user      
      const [date1, user1] = v1.split(" ");
      const [date2, user2] = v2.split(" ");
      const parsedDate1 = new Date(date1);
      const parsedDate2 = new Date(date2);

      //compare by date
      if (!isNaN(parsedDate1) && !isNaN(parsedDate2)) {
        if (parsedDate1.getTime() !== parsedDate2.getTime()) {
          return parsedDate1 - parsedDate2;
        }
      }

      //if dates are the same, compare by username
      if (user1 < user2) return -1;
      if (user1 > user2) return 1;
      return 0;
    } else { //for Good As Of column: compare dates only      
      const parsedDate1 = new Date(v1);
      const parsedDate2 = new Date(v2);

      if (!isNaN(parsedDate1) && !isNaN(parsedDate2)) {
        return parsedDate1 - parsedDate2;
      }
      return 0;
    }
  };

  let columns = [

    //Add status to projectTitle column.
    {
      field: "projectTitle",
      headerName: "Project Title",
      flex: 2,
      renderCell: (params) => {
        const projectTitle = params.row.projectTitle
        const projectStatus = params.row.status?.toLowerCase()
        const isClearLanes = params.row.isClearLanes
        const clearLanesIcon = isClearLanes ? <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '1.5rem', lineHeight: 1, ml: 0.5 }}>*</Typography> : null
        if (projectStatus === "draft") {
          return (
            <>
              <Tooltip title={projectTitle}>
                <StyledTypography variant="body2" key={params.id}>
                  {projectTitle}
                </StyledTypography>
              </Tooltip>
              <DraftIcon aria-hidden={false} aria-label="Draft Project" role="img" style={{ marginLeft: '8px', color: themes.default.palette.draft.dark }} />
              {clearLanesIcon}
            </>
          )
        }
        else if (projectStatus === "active") {
          return (
            <>
              <Tooltip title={projectTitle}>
                <StyledTypography variant="body2" key={params.id}>
                  {projectTitle}
                </StyledTypography>
              </Tooltip>
              <ActiveIcon aria-hidden={false} aria-label="Active Project" role="img" style={{ marginLeft: '8px', color: themes.default.palette.success.dark }} />
              {clearLanesIcon}

              {/* Check if project is marked for review or not and display icon accordingly. */}
              {params.row.isPendingReview && (
                <Tooltip title="This project is due for review. Go to Actions to review.">
                  <Alert
                    variant="none"
                    severity="warning"
                    role="img"
                    aria-label="This project is due for review. Go to Actions to review."
                    style={{ color: themes.default.palette.error.dark }} />
                </Tooltip>
              )}
            </>
          )
        }
        else if (projectStatus === "inactive") {
          return (
            <>
              <Tooltip title={projectTitle}>
                <StyledTypography variant="body2" key={params.id}>
                  {projectTitle}
                </StyledTypography>
              </Tooltip>
              <InactiveIcon aria-hidden={false} aria-label="Inactive Project" role="img" style={{ marginLeft: '8px', color: themes.default.palette.secondary.dark }} />
              {clearLanesIcon}
            </>
          )
        }
        else {
          return (
            <>
              <Tooltip title={projectTitle}>
                <StyledTypography variant="body2" key={params.id}>
                  {projectTitle}
                </StyledTypography>
              </Tooltip>
              {clearLanesIcon}
            </>
          );
        }
      }
    },

    //Show icons as per different project types.
    {
      field: "projectType",
      headerName: "Type",
      width: 100,
      renderCell: (params) => {
        const projectType = params.row.projectType?.toLowerCase();
        if (projectType === "road") {
          return <RoadIcon aria-label="Project Type - Road" />;
        }
        else if (projectType === "regional") {
          return <RegionalIcon aria-label="Project Type - Regional" />;
        }
        else if (projectType === "transit") {
          return <TransitIcon aria-label="Project Type - Transit" />;
        }
        else if (projectType === "regional transit") {
          return <TransitIcon aria-label="Project Type - Regional Transit" />;
        }
      }
    },

    //Show csj or mtp numbers.
    {
      field: "csjNumber",
      headerName: "CSJ or Plan ID",
      width: 190,
      valueGetter: (params) => {
        return params.row.csjNumbers?.join(", ") || "";
      },
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <StyledTypography variant="body2">
            {params.value || ""}
          </StyledTypography>
        </Tooltip>
      )
    },

    //Show strategy types.
    {
      field: "strategyTypesNames",
      headerName: "Strategy Type(s)",
      flex: 1,
      valueGetter: (params) => {
        return params.row.strategyTypesNames?.join(", ") || "";
      },
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <StyledTypography variant="body2">
            {params.value || ""}
          </StyledTypography>
        </Tooltip>
      )
    },

    //Show regions.
    {
      field: "regionNames",
      headerName: "Impacted Region(s)",
      width: 190,
      valueGetter: (params) => {
        let regions = params.row.regionNames?.join(", ") || "";

        // Add impacted regions if present
        const impactedRegions = params.row.impactedRegions?.filter((reg) => reg.id !== null) || [];
        if (impactedRegions.length > 0) {
          const impactedRegionNames = impactedRegions.map((reg) => reg.name).join(", ");
          regions = regions ? `${regions}, ${impactedRegionNames}` : impactedRegionNames;
        }

        return regions;
      },
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <StyledTypography variant="body2">
            {params.value || ""}
          </StyledTypography>
        </Tooltip>
      ),
    },

    //Add Good As Of column.
    {
      field: "reviewedDate",
      headerName: "Good As Of",
      width: 150,
      valueGetter: (params) => {
        return params.row.reviewedDate ? getFormattedDate(new Date(params.row.reviewedDate)) : null;
      },
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || ""}
        </Typography>
      ),
      sortComparator: (v1, v2) => sortByDateAndOptionalUser(v1, v2, false)
    }
  ];

  //Load columns from the database.
  columns = columns.concat(
    [
      {
        "field": "status",
        "headerName": "Project Status"
      },
      {
        field: "isClearLanes",
        headerName: "Clear Lanes Project",
        valueGetter: (params) => params.row.isClearLanes ? "Yes" : "No",
      }
    ]
  );

  //Add Last Modified column by combining last modified date and last modified by.
  columns = columns.concat(
    {
      field: "lastModified",
      headerName: "Last Modified",
      width: 150,
      renderCell: (params) => {

        const lastModifiedDate = params.row.lastModifiedDate ? getFormattedDate(new Date(params.row.lastModifiedDate)) : "";
        const lastModifiedBy = params.row.lastModifiedBy ? params.row.lastModifiedBy.split('@')[0] || params.row.lastModifiedBy : "";
        return (
          <div style={{ whiteSpace: 'normal' }}>
            <Typography variant="body2">
              {lastModifiedDate}
            </Typography>
            <Typography variant="body2">
              {lastModifiedBy}
            </Typography>
          </div>
        );
      },
      valueGetter: (params) => {

        // Return a combined value that contains both date and last modified by
        const lastModifiedDate = new Date(params.row.lastModifiedDate);
        const lastModifiedBy = params.row.lastModifiedBy ? params.row.lastModifiedBy.split('@')[0] || params.row.lastModifiedBy : "";
        const formattedDate = getFormattedDate(lastModifiedDate);
        return `${formattedDate} ${lastModifiedBy}`;
      },
      sortComparator: (v1, v2) => sortByDateAndOptionalUser(v1, v2, true)
    },
  );

  //Add actions column.
  columns = columns.concat(
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 180,
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      export: false,
      getActions: (params) => {

        const { status, id, projectTitle } = params.row;
        const actions = [];
        const loggedInUserRegionIds = loggedInUser?.regionAccessibleSet
          ? loggedInUser.regionAccessibleSet.map(reg => reg.id)
          : [];
        const loggedinUserRole = loggedInUser.roleName?.toLowerCase();
        const projectImpactedRegions = params.row.impactedRegions?.length > 0 ? params.row.impactedRegions.filter(reg => reg.id !== null)?.map(reg => reg.id) : [];

        //Check if any of the loggedin user's region is one of the project's impacted region(s) 
        if ((loggedinUserRole !== "super admin" && loggedinUserRole !== "tti user"
          && projectImpactedRegions && loggedInUserRegionIds.some(userReg => projectImpactedRegions.includes(userReg)))
          || status?.toLowerCase() === "archived") {
          actions.push(
            <GridActionsCellItem
              icon={<Typography>View</Typography>}
              label="View"
              onClick={() => navigate(`/review-project`, { state: { projectId: id, component: "View" } })}
              sx={{ p: 0 }}
            />
          );
        }
        else if (status?.toLowerCase() === "draft") {
          actions.push(
            <GridActionsCellItem
              icon={<IconsWithTooltip title={`View details for ${projectTitle}`} icon={<ViewIcon />} />}
              label={`View details for ${projectTitle}`}
              onClick={() => navigate(`/review-project`, { state: { projectId: id, component: "View" } })}
              sx={{ p: 0 }}
            />,
            <GridActionsCellItem
              icon={<IconsWithTooltip title={`Edit ${projectTitle}`} icon={<EditIcon />} />}
              label={`Edit ${projectTitle}`}
              onClick={() => navigate(`/edit-project?id=${id}`)}
              sx={{ p: 0 }}
            />,
            <GridActionsCellItem
              icon={<IconsWithTooltip title={`Delete ${projectTitle}`} icon={<DeleteIcon />} />}
              label={`Delete ${projectTitle}`}
              onClick={() => handleDeleteSelected([params.row.id])}
              sx={{ p: 0 }}
            />);
        }
        else if (status?.toLowerCase() === "active" || status?.toLowerCase() === "inactive") {
          actions.push(
            <GridActionsCellItem
              icon={<IconsWithTooltip title={`View details for ${projectTitle}`} icon={<ViewIcon />} />}
              label={`View details for ${projectTitle}`}
              onClick={() => navigate(`/review-project`, { state: { projectId: id, component: "View" } })}
              sx={{ p: 0 }}
            />,
            <GridActionsCellItem
              icon={<IconsWithTooltip title={`Review ${projectTitle}`} icon={<ReviewIcon />} />}
              label={`Review ${projectTitle}`}
              onClick={() => navigate(`/review-project`, { state: { projectId: id, component: "Review" } })}
              sx={{ p: 0 }}
            />,
            <GridActionsCellItem
              icon={<IconsWithTooltip title={`Edit ${projectTitle}`} icon={<EditIcon />} />}
              label={`Edit ${projectTitle}`}
              onClick={() => navigate(`/edit-project?id=${id}`)}
              sx={{ p: 0 }}
            />,
            <GridActionsCellItem
              icon={<IconsWithTooltip title={`Unpublish ${projectTitle}`} icon={<UnpublishIcon />} />}
              label={`Unpublish ${projectTitle}`}
              onClick={() => handleUnpublishSelected([params.row.id])}
              sx={{ p: 0 }}
            />
          );
        }
        return actions;
      }
    }
  );
  //508 compliance: Ensure action buttons and sort buttons are keyboard accessible by setting tabindex to 0.
  //  Since MUI DataGrid does not provide a built-in way to set tabindex for these elements,
  //  we use a MutationObserver to watch for changes in the grid and update the tabindex accordingly.
  const gridRef = useRef(null);
  useEffect(() => {
    if (!gridRef.current) return;

    const observer = new MutationObserver(() => {
      // Disconnect first to prevent infinite loop
      observer.disconnect();

      const patchTabIndex = () => {
        if (!gridRef.current) return;

        // Sort buttons
        const sortButtons = gridRef.current.querySelectorAll(
          '.MuiDataGrid-iconButtonContainer button'
        );
        sortButtons.forEach((btn) => btn.setAttribute('tabindex', '0'));

        // Action cell menu container
        const menuContainers = gridRef.current.querySelectorAll(
          '.MuiDataGrid-actionsCell[role="menu"]'
        );
        menuContainers.forEach((el) => el.setAttribute('tabindex', '0'));

        // Action cell buttons
        const actionButtons = gridRef.current.querySelectorAll(
          '.MuiDataGrid-actionsCell button'
        );
        actionButtons.forEach((btn) => btn.setAttribute('tabindex', '0'));
      };

      patchTabIndex();

      // Reconnect after patching
      observer.observe(gridRef.current, {
        subtree: true,
        attributes: true,
        attributeFilter: ['tabindex'],
      });
    });

    // Initial patch without observer
    const sortButtons = gridRef.current.querySelectorAll(
      '.MuiDataGrid-iconButtonContainer button'
    );
    sortButtons.forEach((btn) => btn.setAttribute('tabindex', '0'));

    const menuContainers = gridRef.current.querySelectorAll(
      '.MuiDataGrid-actionsCell[role="menu"]'
    );
    menuContainers.forEach((el) => el.setAttribute('tabindex', '0'));

    const actionButtons = gridRef.current.querySelectorAll(
      '.MuiDataGrid-actionsCell button'
    );
    actionButtons.forEach((btn) => btn.setAttribute('tabindex', '0'));

    // Start observing
    observer.observe(gridRef.current, {
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex'],
    });

    return () => observer.disconnect();
  }, [filteredRows, columns]);


  return (
    <>

      {/* Breadcrumbs for the page */}
      <Grid item xs={12} md={12}>
        <RouterBreadcrumbs pathItems={pathItems} />
      </Grid>

      {/* Banner with important notice */}
      <Grid item xs={12} sx={{ paddingRight: 3, paddingTop: 3 }}>
        <Alert severity="warning">
          <StyledNoticeTypography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>Important:</StyledNoticeTypography>
          <StyledNoticeTypography variant="body2">
            Please ensure <strong>all</strong> draft projects have been reviewed, connected to a roadway, and published <strong>with a date stamp in the "Good As Of" column.</strong>
          </StyledNoticeTypography>
          <StyledNoticeTypography variant="body2">
            A project will not be displayed in a Mitigation Plan unless these steps have been taken.
          </StyledNoticeTypography>
          <StyledNoticeTypography variant="body2">
            Unnecessary draft projects may be deleted.
          </StyledNoticeTypography>
        </Alert>
      </Grid>

      {/* Page Title for the page */}
      <Grid item xs={12} md={12}>
        <div style={{ display: 'flex' }}>
          <Title title="View Projects" />
          <Tooltip title="Hover over a legend item, icon, or text for more information" placement='right'>
            <InfoIcon aria-label="Legend information" fontSize="small" style={{ alignSelf: 'center', marginLeft: 8, marginTop: 8 }} />
          </Tooltip>
        </div>
      </Grid>

      {/* Project Status & Type Labels */}
      <Grid container sx={{ marginBottom: "4px" }}>
        <Grid item lg="auto" md={3} sm={3} xs={12} sx={{ paddingRight: '16px' }}>
          <div style={{ display: 'flex', color: themes.default.palette.draft.dark }}>
            <Tooltip title="Projects not yet published.">
              <Box display="flex" alignItems="center">
                <DraftIcon style={{ marginRight: '8px' }} />
                <Typography component="div">
                  Draft
                </Typography>
              </Box>
            </Tooltip>
          </div>
        </Grid>

        <Grid item lg="auto" md={3} sm={3} xs={12} sx={{ paddingRight: '16px' }}>
          <div style={{ display: 'flex', color: themes.default.palette.success.dark }}>
            <Tooltip title="Published and viewable project connected to a top 105 roadway.">
              <Box display="flex" alignItems="center">
                <ActiveIcon style={{ marginRight: '8px' }} />
                <Typography>
                  Active
                </Typography>
              </Box>
            </Tooltip>
          </div>
        </Grid>
        <Grid item lg="auto" md={3} sm={3} xs={12} sx={{ paddingRight: '16px' }}>
          <div style={{ display: 'flex', color: themes.default.palette.secondary.dark }}>
            <Tooltip title="Published and viewable project not connected to a top 105 roadway.">
              <Box display="flex" alignItems="center">
                <InactiveIcon style={{ marginRight: '8px' }} />
                <Typography>
                  Inactive
                </Typography>
              </Box>
            </Tooltip>
          </div>
        </Grid>
        <Grid item lg="auto" md={3} sm={3} xs={12}>
          <div style={{ display: 'flex', color: themes.default.palette.error.dark }}>
            <Tooltip title="Projects due for review.">
              <Box display="flex" alignItems="center">
                <PendingReviewIcon style={{ marginRight: '8px' }} />
                <Typography>
                  Needs Review
                </Typography>
              </Box>
            </Tooltip>
          </div>
        </Grid>
      </Grid>

      {/* Project Type Labels */}
      <Grid container sx={{ marginBottom: "20px" }}>
        <Grid item lg="auto" md={3} sm={3} xs={12} sx={{ paddingRight: '16px' }}>
          <div style={{ display: 'flex' }}>
            <Tooltip title="Projects relating primarily to roadway infrastructure.">
              <Box display="flex" alignItems="center">
                <RoadIcon style={{ marginRight: '8px' }} />
                <Typography>
                  Road
                </Typography>
              </Box>
            </Tooltip>
          </div>
        </Grid>

        <Grid item lg="auto" md={3} sm={3} xs={12} sx={{ paddingRight: '16px' }}>
          <div style={{ display: 'flex' }}>
            <Tooltip title=" Projects or programs that apply through a region or are not specifically tied to specific locations.">
              <Box display="flex" alignItems="center">
                <RegionalIcon style={{ marginRight: '8px' }} />
                <Typography>
                  Regional
                </Typography>
              </Box>
            </Tooltip>
          </div>
        </Grid>

        <Grid item lg="auto" md={3} sm={3} xs={12} sx={{ paddingRight: '16px' }}>
          <div style={{ display: 'flex' }}>
            <Tooltip title="Transit relates to specific and isolated transit projects whereas Regional Transit relates to transit projects with a regional scale unassociated with roadways.">
              <Box display="flex" alignItems="center">
                <TransitIcon style={{ marginRight: '8px' }} />
                <Typography>
                  Transit or Regional Transit
                </Typography>
              </Box>
            </Tooltip>
          </div>
        </Grid>

        <Grid item lg="auto" md={3} sm={3} xs={12}>
          <div style={{ display: 'flex' }}>
            <Tooltip title="Project is a Texas Clear Lanes project.">
              <Box display="flex" alignItems="center">
                <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '1.5rem', lineHeight: 1, marginRight: '8px' }}>*</Typography>
                <Typography>
                  Clear Lanes Project
                </Typography>
              </Box>
            </Tooltip>
          </div>
        </Grid>
      </Grid>

      {/* Checkboxes and buttons */}
      <Grid item lg={3} md={4} sm={4} xs={12} style={{ display: 'flex', flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Checkbox checked={allProjectsChecked} onChange={handleShowAllProjects} />}
          label="Show All Projects"
        />
        <div style={{ display: 'flex', marginRight: "16px" }}>
          <Tooltip title="Archived Projects are completed projects that no longer appear on a mitigation plan. Projects are automatically archived 2 years after completion" placement='right'>
            <Box display="flex" alignItems="center">
              <FormControlLabel
                control={<Checkbox checked={archiveChecked} onChange={handleShowArchivedProjects} />}
                label="Show Archived Projects"
                sx={{ marginRight: "5px" }}
              />
              <InfoIcon aria-label="Archived projects information" fontSize="small" style={{ verticalAlign: 'middle' }} />
            </Box>
          </Tooltip>
        </div>
        <FormControlLabel
          control={<Checkbox checked={reviewPendingChecked} onChange={handleShowReviewPendingProjects} />}
          label="Needs Review"
        />
      </Grid>

      {/* Data Table */}
      <Grid container sx={{ paddingRight: 3 }} ref={gridRef}>

        <StyledDataGridProjects
          aria-label="Projects table"
          rows={filteredRows || []}
          columns={columns}
          disableColumnMenu
          autoHeight
          loading={fetcher.isLoading}
          columnVisibilityModel={{ status: false, isClearLanes: false }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              totalCount: projectsData?.length || 0,
              filter: {
                rowsCount: (filteredRows ?? []).length,
                columns: columns,
                active: activeFilter,
                onChange: (val) => setActiveFilter(val)
              },
              onExportClick: handleExportCSV,
              exportedFilename: "Projects_List"
            },
            pagination: {
              labelRowsPerPage: 'Rows per page:',
              SelectProps: {
                native: true,
                inputProps: {
                  'aria-label': 'Rows per page',
                  'id': 'pagination-select-input'
                }
              }
            },
          }}
          initialState={{
            columns: {

              // Hide column status from data grid view.
              columnVisibilityModel: {
                status: false,
                isClearLanes: false,
              },
              sorting: {
                sortModel: [{ field: 'lastModified' }],
              },
            },
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 15, 20, 25]}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setSelectedRows(newRowSelectionModel);
          }}
          rowSelectionModel={selectedRows}
        />
      </Grid>

      {/* ConfirmationDialog component */}
      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() => setConfirmationDialog((prevState) => ({ ...prevState, open: false }))}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        onConfirm={confirmationDialog.onConfirm}
      />
    </>
  );
}